import { Flex, Text } from '@chakra-ui/react'
import React from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'
import { PhoneNumber } from '@mangovoice/types'

import ExportCsvButton from '../components/ExportCsvButton'
import { useReportingI18n } from '../i18n'
import { DateRange } from '../shared/DateRange'
import { DateTimeRangeDropdown } from '../shared/DateTimeRangeDropdown'
import { MenuPlacement } from '../shared/dropdown/Menu'
import { PhoneNumberMultiDropdown } from '../shared/PhoneNumberMultiDropdown'

export type ReportingFiltersProps = {
  titleKey: string
  titleFallback: string
  subtitleKey: string
  subtitleFallback: string
  dateTimeRange?: DateRange
  onValueChange: (newRange: DateRange) => void
  selectedPhoneNumbers: PhoneNumber[]
  phoneNumbers: PhoneNumber[]
  isPhoneNumbersLoading: boolean
  onPhoneNumberChange?: (phoneNumbers: PhoneNumber[]) => void
  onExportCsv?: () => void
  isExportCsvDisabled?: boolean
}

export const ReportingFilters = ({
  titleKey,
  titleFallback,
  subtitleKey,
  subtitleFallback,
  dateTimeRange,
  onValueChange,
  selectedPhoneNumbers,
  onPhoneNumberChange,
  phoneNumbers,
  isPhoneNumbersLoading,
  onExportCsv,
  isExportCsvDisabled = true,
}: ReportingFiltersProps) => {
  const { text } = useReportingI18n()
  return (
    <Flex
      width="calc(100% + 2.5rem)"
      marginLeft="-1.25rem"
      marginRight="-1.25rem"
      marginTop="-1.25rem"
      flexDirection="column"
      background="bg.default"
    >
      <Flex
        padding="0.75rem 1.25rem"
        flexDirection="column"
        borderBottom="1px solid var(--chakra-colors-border-default)"
      >
        <Text fontSize="1.25rem" fontWeight="800" color="fg.default">
          {text(titleKey, titleFallback)}
        </Text>
        <Text {...textStyles[textStyleKeys.Body2]} color="fg.subtle">
          {text(subtitleKey, subtitleFallback)}
        </Text>
      </Flex>
      <Flex
        padding="0.75rem 1.25rem"
        alignItems="flex-end"
        gap="1.5rem"
        borderBottom="2px solid var(--chakra-colors-border-default)"
      >
        <Flex flexDirection="column" gap="0.25rem">
          <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
            {text('reportingFilters.dateRange', 'Date Range')}
          </Text>
          <DateTimeRangeDropdown
            onValueChange={onValueChange}
            dateTimeRange={dateTimeRange}
            useExtendedDates={true}
            menuPlacement={MenuPlacement.BottomLeft}
          />
        </Flex>
        <Flex flexDirection="column" gap="0.25rem">
          <Flex alignItems="center" gap="0.5rem">
            <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
              {text('reportingFilters.number', 'Number')}
            </Text>
            {selectedPhoneNumbers.length >= 50 && (
              <Text fontSize="11px" color="fg.muted" fontStyle="italic">
                {text('reportingFilters.maxPhoneNumbers', '(max {{max}})', {
                  max: 50,
                })}
              </Text>
            )}
          </Flex>
          <PhoneNumberMultiDropdown
            selectedPhoneNumbers={selectedPhoneNumbers}
            phoneNumbers={phoneNumbers}
            isPhoneNumbersLoading={isPhoneNumbersLoading}
            onSelectionChange={onPhoneNumberChange}
            width="200px"
          />
        </Flex>
        <ExportCsvButton onClick={onExportCsv} disabled={isExportCsvDisabled} />
      </Flex>
    </Flex>
  )
}
